
import { createContext, FC, ReactNode, useContext, useEffect, useState } from "react";
import { Client } from "@searchspring/snap-client";
import { Tracker } from "@searchspring/snap-tracker";
import { UrlManager, UrlTranslator } from "@searchspring/snap-url-manager";
import { SearchStore, SearchStoreConfig } from "@searchspring/snap-store-mobx";
import { EventManager } from "@searchspring/snap-event-manager";
import { Logger } from "@searchspring/snap-logger";
import { Profiler } from "@searchspring/snap-profiler";
import { SearchController } from "@searchspring/snap-controller";
import { Facet, SearchspringSearchResponse, SearchspringSortOption } from "../lib/searchspringTypes";
import { 
  SearchRequestModelFilterTypeEnum, 
  SearchRequestModelFilterValue,  
  SearchResponseModelFilterValue,  
  SearchResponseModelPagination, 
  SearchResponseModelResult 
} from "@searchspring/snapi-types";
import { PLPViewOption } from "../components/SearchspringPLP/PLPViewSelector";
import useContentful from "../hooks/useContentful";
import { ContentfulPLPFilterConfiguration } from "../lib/contentfulTypes";

interface SearchspringPLPContextValue {
  activeFilters: SearchResponseModelFilterValue[];
  activeSortOption: string;
  clearPLPFilters: () => void;
  compareActive: boolean;
  compareProducts: SearchResponseModelResult[];
  compareMax: number;
  facets: Facet[];
  fetchNextPage: () => void;
  filterConfig: ContentfulPLPFilterConfiguration["fields"];
  filterMenuOpen: boolean;
  filterPLP: (field: string, option: any, type: string) => void;
  handleCompareProductClick: (product: any) => void;
  pagination: SearchResponseModelPagination;
  products: SearchResponseModelResult[];
  relatedSearchTerms: string[];
  sortMenuOpen: boolean;
  sortOptions: SearchspringSortOption[];
  sortPLP: (option: SearchspringSortOption) => void;
  toggleCompare: () => void;
  toggleFilterMenu: () => void;
  toggleSortMenu: () => void;
  toggleViewOption: (layout: PLPViewOption) => void;
  viewOption: PLPViewOption;
  collectionCompareEnable?: string;
}

const SearchspringPLPContext =  createContext<SearchspringPLPContextValue>({
  activeFilters: [],
  activeSortOption: '',
  clearPLPFilters: () => {},
  compareActive: false,
  compareProducts: [],
  compareMax: 2,
  facets: [],
  fetchNextPage: () => {},
  filterConfig: {} as ContentfulPLPFilterConfiguration["fields"],
  filterMenuOpen: false,
  filterPLP: () => {},
  handleCompareProductClick: () => {},
  pagination: {},
  products: [],
  relatedSearchTerms: [],
  sortMenuOpen: false,
  sortOptions: [],
  sortPLP: () => {},
  toggleCompare: () => {},
  toggleFilterMenu: () => {},
  toggleSortMenu: () => {},
  toggleViewOption: () => {},
  viewOption: 'grid',
});

export const useSearchspringPLPContext = () => {
  return useContext(SearchspringPLPContext);
}

interface ProviderProps {
  children?: ReactNode;
  contentfulFilterConfigId?: string;
  // This context also serves the search results page, which is not filtered by collection.
  shopifyCollectionHandle?: string;
  siteId: string;
  collectionCompareEnable?: string;
  shouldSliceVariants?: boolean;
  searchTerms?: string;
}

export const SearchspringPLPContextProvider: FC<ProviderProps> = ({ 
  children, 
  siteId, 
  shopifyCollectionHandle, 
  contentfulFilterConfigId = '',
  collectionCompareEnable,
  shouldSliceVariants = false,
  searchTerms = ''
}) => {
  const entryType = shouldSliceVariants ? 'variant' : 'product';
  const entryTypeFilter: SearchRequestModelFilterValue = {
    background: true,
    field: 'ss_entry_type',
    type: 'value' as SearchRequestModelFilterTypeEnum,
    value: entryType
  }
  const baseFilters: SearchRequestModelFilterValue[] = [entryTypeFilter];

  // This filters all subsequent requests by the Shopify collection handle.
  let backgroundFilter: SearchRequestModelFilterValue;
  if (shopifyCollectionHandle) {
    backgroundFilter = {
      background: true,
      field: 'collection_handle',
      type: 'value' as SearchRequestModelFilterTypeEnum,
      value: shopifyCollectionHandle,
    };
    baseFilters.push(backgroundFilter);
  }

  const [searchController, setSearchController] = useState<SearchController>();
  const [products, setProducts] = useState<SearchResponseModelResult[]>([]);
  const [facets, setFacets] = useState<Facet[]>([]);
  const [activeFilters, setActiveFilters] = useState<SearchResponseModelFilterValue[]>([]);
  const [pagination, setPagination] = useState<SearchResponseModelPagination>({});
  const [sortOptions, setSortOptions] = useState<SearchspringSortOption[]>([]);
  const [viewOption, setViewOption] = useState<PLPViewOption>('grid');
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [compareActive, setCompareActive] = useState(false);
  const [compareProducts, setCompareProducts] = useState([]);
  const initialCompareMax = window.innerWidth >= 1024 ? 4 : 2;
  const [compareMax, setCompareMax] = useState(initialCompareMax);
  const [relatedSearchTerms, setRelatedSearchTerms] = useState([]);
  // The active sort option is returned as part of the sort request, but the wait for the
  // API response makes sorting feel sluggish. Keeping track of this here allows updating
  // the active styling immediately while the sort request runs asynchronously.
  const [activeSortOption, setActiveSortOption] = useState('');

  let filterConfig = {} as ContentfulPLPFilterConfiguration["fields"];
  if (contentfulFilterConfigId) {
    const { contentfulData } = useContentful({'sys.id': contentfulFilterConfigId, include: 10});
    if (contentfulData && contentfulData.items && contentfulData.items[0]) {
      filterConfig = contentfulData.items[0].fields;
    }
  }

  useEffect(() => {
    const handleResize = () => setCompareMax(window.innerWidth >= 1024 ? 4 : 2);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize search controller.
  useEffect(() => {
    // By default, Searchsprings looks for a "q" url parameter and applies
    // it as a request parameter, so this works out of the box. If that param
    // is ever changed to something other than "q," it will need changed here.
    const urlManager = new UrlManager(new UrlTranslator());

    const config: SearchStoreConfig = {
      id: 'search',
      globals: {
        filters: baseFilters,
        sorts: [],
        pagination: {
          pageSize: 20
        }
      },
      settings: {
        infinite: {}
      }
    };
  
    const services = {
      client: new Client({ siteId }),
      eventManager: new EventManager(),
      logger: new Logger(),
      profiler: new Profiler(),
      store: new SearchStore(config, { urlManager }),
      tracker: new Tracker({ siteId }),
      urlManager,
    };

    const controller = new SearchController(config, services);
    // Handle search results.
    controller.on('afterSearch', async (eventData: { response: SearchspringSearchResponse }, next) => {
      const results = eventData.response.results || [];
      const pageFetched = eventData.response.pagination?.page || 1;
      if (pageFetched > 1) {
        setProducts(currentProducts => [...currentProducts, ...results]);
      } else {
        setProducts(results);
      }

      setActiveFilters(eventData.response.filters || []);
      setSortOptions(eventData.response.meta.sortOptions || []);
      setPagination(eventData.response.pagination || {});

      // Facet data is spread across two different objects in the response.
      // Unify them before storing in state for ease of use in components.
      const parsedFacets = eventData.response.facets?.map(facet => ({
        ...facet,
        ...eventData.response.meta.facets?.[`${facet.field}`]
      })) || [];
      setFacets(parsedFacets);

      await next();
    });

    setSearchController(controller);
    // Perform initial search.
    controller.search();

    if (searchTerms) {
      fetchRelatedSearches();
    }
  }, []);

  const fetchRelatedSearches = () => {
    fetch(`https://${siteId}.a.searchspring.io/api/suggest/query?siteId=${siteId}&query=${searchTerms}&suggestionCount=5&integratedSpellCorrection=true`)
      .then(res => res.json())
      .then(res => {
        if (res && res.alternatives && res.alternatives.length > 0) {
          setRelatedSearchTerms(res.alternatives.map((a: any) => a.text));
        }
      });
  }

  const filterPLP = (field: string, option: any, type: string) => {
    if (!searchController?.config.globals?.filters) {
      return;
    }

    if (type == 'range-buckets' || type == 'range') {
      let filterIndex = -1;
      
      // This should be refactored post-launch, but currently these objects come in differently.
      if (type == 'range-buckets') {
        const lowValue = (option?.low && option.low >= 0) ? option.low : '*';
        const highValue = (option?.high && option.high >= 0) ? option.high : '*';
        console.log(lowValue, highValue);
        filterIndex = searchController.config.globals.filters.findIndex(
          (i: any) => i.field == field && i.value?.low == lowValue && i.value?.high == highValue
        );
      }
      if (type == 'range') {
        const lowValue = (option?.value?.low && option.value.low >= 0) ? option.value.low : '*';
        const highValue = (option?.value?.high && option.value.high >= 0) ? option.value.high : '*';
        filterIndex = searchController.config.globals.filters.findIndex(
          (i: any) => i.field == field && i.value?.low == lowValue && i.value?.high == highValue
        );
      }

      if (filterIndex >= 0) {
        searchController.config.globals.filters.splice(filterIndex, 1);
      } else {
        const filter = {
          field, 
          value: {low: option?.low || '*', high: option?.high || '*'},
          type: 'range',
        };
        // @ts-ignore
        searchController.config.globals.filters.push(filter);
      }
    } else {
      const filterIndex = searchController.config.globals.filters.findIndex(
        (i: SearchRequestModelFilterValue) => i.field == field && i.value == option.value
      );
      // If this filter already exists array, it is "active" and should be removed.
      if (filterIndex >= 0) {
        searchController.config.globals.filters.splice(filterIndex, 1);
      } else {
        const filter = {
          field, 
          value: option.value, 
          type: type as SearchRequestModelFilterTypeEnum,
        };
        searchController.config.globals.filters.push(filter);
      }
    }

    searchController.search();
  }

  const clearPLPFilters = () => {
    if (!searchController?.config.globals) {
      return;
    }

    searchController.config.globals.filters = baseFilters;
    searchController.search();
  }
  
  const sortPLP = (option: SearchspringSortOption) => {
    if (!searchController?.config.globals?.sorts) {
      return;
    }

    setActiveSortOption(`${option.field}-${option.direction}`);
    // Getting this type to work here is more trouble than its worth.
    // Both the incoming type and the target type have a distinct enum
    // for direction, both with a value of 'asc' | 'desc'.
    // @ts-ignore 
    searchController.config.globals.sorts = [option];
    searchController.search();
  }

  const fetchNextPage = () => {
    if (!searchController?.store.pagination.next) {
      return;
    }
    
    // When config.settings.infinite is defined and store.pagination.next.url.go({ history: 'replace' }) is invoked, 
    // the next page will be fetched and its result set will be appended to the existing result set.
    searchController.store.pagination.next.url.go({ history: 'replace' });
  }

  const toggleCompare = () => setCompareActive(!compareActive);
  const toggleFilterMenu = () => setFilterMenuOpen(!filterMenuOpen);
  const toggleSortMenu = () => setSortMenuOpen(!sortMenuOpen);
  const toggleViewOption = (layout: PLPViewOption) => setViewOption(layout);

  const handleCompareProductClick = (product: any) => {
    // @ts-ignore
    const productIndex = compareProducts.findIndex(i => i.attributes.ss_id == product.attributes.ss_id);
    
    if (productIndex >= 0) {
      // @ts-ignore
      setCompareProducts([
        ...compareProducts.slice(0, productIndex), 
        ...compareProducts.slice(productIndex + 1)]
      );
    } else if (compareProducts.length < compareMax) {
      // @ts-ignore
      setCompareProducts([...compareProducts, product]);
    }
  }
  
  const contextValue: SearchspringPLPContextValue = {
    activeFilters,
    activeSortOption,
    clearPLPFilters,
    compareActive,
    compareProducts,
    compareMax,
    facets,
    fetchNextPage,
    filterConfig,
    filterMenuOpen,
    filterPLP,
    handleCompareProductClick,
    pagination,
    products,
    relatedSearchTerms,
    sortMenuOpen,
    sortOptions,
    sortPLP,
    toggleCompare,
    toggleFilterMenu,
    toggleSortMenu,
    toggleViewOption,
    viewOption,
    collectionCompareEnable
  };

  return (
    <SearchspringPLPContext.Provider value={contextValue}>
      {children}
    </SearchspringPLPContext.Provider>
  ); 
}